import { useState, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Eye, ChevronRight, Shuffle, Printer, Loader2, Lightbulb, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BIG = "font-patient font-black text-gray-800";
const Next = ({ onClick, label = "Suivant", testid = "gg-next" }) => (
  <Button onClick={onClick} data-testid={testid} className="h-16 px-8 rounded-2xl text-2xl font-patient font-black text-white" style={{ background: "#2E8EC2" }}>
    {label} <ChevronRight className="w-7 h-7 ml-1" />
  </Button>
);
const Reveal = ({ onClick, label = "Révéler la réponse" }) => (
  <Button onClick={onClick} variant="outline" data-testid="gg-reveal" className="h-16 px-8 rounded-2xl text-2xl font-patient font-bold border-4">
    <Eye className="w-7 h-7 mr-2" /> {label}
  </Button>
);
const Counter = ({ i, n }) => <p className="text-xl font-patient text-gray-500 mb-4" data-testid="gg-counter">{i + 1} / {n}</p>;
const Done = ({ onEnd }) => (
  <div className="text-center py-10" data-testid="gg-done">
    <p className={`${BIG} text-4xl mb-6`}>Bravo à tout le groupe !</p>
    <Next onClick={onEnd} label="Terminer la séance" testid="gg-finish-inline" />
  </div>
);

function useStep(items) {
  const [i, setI] = useState(0);
  const [shown, setShown] = useState(false);
  return { i, it: items[i], shown, show: () => setShown(true), next: () => { setI((x) => x + 1); setShown(false); }, end: i >= items.length };
}

export function MissingWord({ game, onEnd }) {
  const s = useStep(game.items);
  if (s.end) return <Done onEnd={onEnd} />;
  const parts = (s.it.text || "").split(/_{2,}/);
  return (
    <div className="text-center" data-testid="gg-missing-word">
      <Counter i={s.i} n={game.items.length} />
      <p className="text-2xl font-patient font-bold text-amber-700 mb-4">{s.it.kind}</p>
      <p className={`${BIG} text-4xl sm:text-6xl leading-tight mb-10`}>
        {parts.map((p, k) => (
          <span key={k}>{p}{k < parts.length - 1 && (
            <span className={`inline-block min-w-[4ch] mx-2 px-3 rounded-2xl ${s.shown ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-100"}`} data-testid="gg-answer">{s.shown ? s.it.answer : "…"}</span>
          )}</span>
        ))}
      </p>
      {s.shown ? <Next onClick={s.next} /> : <Reveal onClick={s.show} />}
    </div>
  );
}

export function TurnQuiz({ game, players, onEnd, award }) {
  const s = useStep(game.items);
  const [picked, setPicked] = useState(null);
  if (s.end) return <Done onEnd={onEnd} />;
  const who = players.length ? players[s.i % players.length] : null;
  const choose = (k) => {
    if (picked !== null) return;
    setPicked(k);
    if (k === s.it.answer) award(who?.id);
  };
  return (
    <div className="text-center" data-testid="gg-turn-quiz">
      <Counter i={s.i} n={game.items.length} />
      {who && <p className="text-3xl font-patient font-black text-emerald-700 mb-4" data-testid="gg-turn">C'est au tour de {who.name}</p>}
      <p className={`${BIG} text-4xl sm:text-5xl mb-8`}>{s.it.question}</p>
      <div className="grid gap-4 max-w-3xl mx-auto mb-8">
        {(s.it.options || []).map((o, k) => (
          <button key={k} onClick={() => choose(k)} data-testid={`gg-option-${k}`}
            className={`h-20 rounded-3xl border-4 text-3xl font-patient font-bold transition-colors ${picked === null ? "bg-white border-gray-200" : k === s.it.answer ? "bg-emerald-100 border-emerald-500 text-emerald-800" : k === picked ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-white border-gray-100 text-gray-400"}`}>{o}</button>
        ))}
      </div>
      {picked !== null && <Next onClick={() => { setPicked(null); s.next(); }} />}
    </div>
  );
}

export function Riddles({ game, onEnd }) {
  const s = useStep(game.items);
  const [n, setN] = useState(1);
  if (s.end) return <Done onEnd={onEnd} />;
  const clues = s.it.clues || [];
  return (
    <div className="text-center" data-testid="gg-riddles">
      <Counter i={s.i} n={game.items.length} />
      <p className="text-3xl font-patient font-black text-amber-700 mb-6">Qui suis-je ?</p>
      <ol className="space-y-4 max-w-4xl mx-auto mb-8 text-left">
        {clues.slice(0, n).map((c, k) => <li key={k} className={`${BIG} text-3xl sm:text-4xl soft-in`}>{k + 1}. {c}</li>)}
      </ol>
      {s.shown && <p className="text-5xl font-patient font-black text-emerald-700 mb-8" data-testid="gg-answer">{s.it.answer}</p>}
      <div className="flex flex-wrap justify-center gap-4">
        {!s.shown && n < clues.length && (
          <Button onClick={() => setN(n + 1)} variant="outline" data-testid="gg-clue" className="h-16 px-8 rounded-2xl text-2xl font-patient font-bold border-4">
            <Lightbulb className="w-7 h-7 mr-2" /> Indice suivant
          </Button>
        )}
        {s.shown ? <Next onClick={() => { setN(1); s.next(); }} /> : <Reveal onClick={s.show} />}
      </div>
    </div>
  );
}

export function PhotoMemories({ game, onEnd }) {
  const s = useStep(game.items);
  const [q, setQ] = useState(0);
  if (s.end) return <Done onEnd={onEnd} />;
  const qs = s.it.questions || [];
  return (
    <div className="text-center" data-testid="gg-photo">
      <Counter i={s.i} n={game.items.length} />
      {s.it.image_url && <img src={s.it.image_url} alt={s.it.title} className="mx-auto max-h-[38vh] rounded-3xl shadow-xl mb-6 object-contain" />}
      <p className={`${BIG} text-4xl mb-4`}>{s.it.title}</p>
      {qs[q] && <p className="text-3xl font-patient font-bold text-emerald-800 mb-8 soft-in" key={q} data-testid="gg-question">{qs[q]}</p>}
      {q < qs.length - 1 ? <Next onClick={() => setQ(q + 1)} label="Question suivante" testid="gg-next-question" /> : <Next onClick={() => { setQ(0); s.next(); }} label="Photo suivante" />}
    </div>
  );
}

export function Loto({ game, onEnd }) {
  const items = game.items;
  const [drawn, setDrawn] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [seen, setSeen] = useState(false);
  const audio = useRef(null);
  const sound = game.type === "sound_loto";
  const left = items.map((_, k) => k).filter((k) => !drawn.includes(k));
  const cur = drawn.length ? items[drawn[drawn.length - 1]] : null;
  const play = (it) => {
    if (!it?.audio_url) return;
    audio.current?.pause();
    audio.current = new Audio(`${process.env.REACT_APP_BACKEND_URL}${it.audio_url}`);
    audio.current.play().catch(() => {});
  };
  const draw = () => {
    const k = left[Math.floor(Math.random() * left.length)];
    setDrawn((d) => [...d, k]); setSeen(false);
    if (sound) play(items[k]);
  };
  const print = async () => {
    setPrinting(true);
    try {
      const { data } = await api.get(`/group-games/${game.id}/loto/pdf`, { params: { cards: 8 }, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = "cartons-loto.pdf"; a.click(); URL.revokeObjectURL(url);
      toast.success("8 cartons et la liste de tirage sont prêts à imprimer.");
    } catch { toast.error("Impression impossible."); } finally { setPrinting(false); }
  };
  return (
    <div className="text-center" data-testid="gg-loto">
      <Button variant="outline" onClick={print} disabled={printing} data-testid="gg-loto-print" className="mb-6 h-12 rounded-2xl font-bold">
        {printing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Printer className="w-5 h-5 mr-2" />} Imprimer les cartons
      </Button>
      {cur && sound && !seen ? (
        <div className="soft-in mb-6" key={drawn.length} data-testid="gg-sound-current">
          <p className={`${BIG} text-4xl mb-6`}>Écoutez bien… Qu'est-ce que c'est ?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline" onClick={() => play(cur)} data-testid="gg-sound-replay" className="h-16 px-8 rounded-2xl text-2xl font-patient font-bold border-4">
              <Volume2 className="w-7 h-7 mr-2" /> Réécouter
            </Button>
            <Reveal onClick={() => setSeen(true)} label="Montrer l'image" />
          </div>
        </div>
      ) : cur ? (
        <div className="soft-in" key={drawn.length} data-testid="gg-loto-current">
          {cur.image_url && <img src={cur.image_url} alt={cur.label} className="mx-auto max-h-[36vh] rounded-3xl shadow-xl mb-4 object-contain" />}
          <p className={`${BIG} text-5xl mb-6`}>{cur.label}</p>
        </div>
      ) : <p className={`${BIG} text-4xl mb-8`}>{sound ? "Distribuez les cartons, puis faites écouter le premier son." : "Distribuez les cartons, puis tirez la première image."}</p>}
      {sound && cur && !seen ? null : left.length ? (
        <Button onClick={draw} data-testid="gg-loto-draw" className="h-16 px-8 rounded-2xl text-2xl font-patient font-black text-white" style={{ background: "#2E8EC2" }}>
          <Shuffle className="w-7 h-7 mr-2" /> {sound ? "Son suivant" : "Tirer une image"} ({left.length})
        </Button>
      ) : <Done onEnd={onEnd} />}
      {drawn.length > 1 && (
        <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="gg-loto-history">
          {drawn.slice(0, -1).map((k) => <span key={k} className="px-3 h-9 inline-flex items-center rounded-full bg-white border text-lg font-patient">{items[k].label}</span>)}
        </div>
      )}
    </div>
  );
}

export function PetitBac({ game, onEnd }) {
  const it = game.items[0] || { categories: [], letters: [] };
  const [used, setUsed] = useState([]);
  const left = (it.letters || []).filter((l) => !used.includes(l));
  const cur = used[used.length - 1];
  return (
    <div className="text-center" data-testid="gg-petit-bac">
      <p className="text-6xl sm:text-8xl font-patient font-black text-amber-600 mb-6 min-h-[1.2em]" data-testid="gg-letter">{cur || "?"}</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto mb-8 text-left">
        {(it.categories || []).map((c) => <div key={c} className={`rounded-2xl bg-white border-4 border-gray-100 px-6 py-4 ${BIG} text-3xl`}>{c}</div>)}
      </div>
      {left.length ? (
        <Button onClick={() => setUsed((u) => [...u, left[Math.floor(Math.random() * left.length)]])} data-testid="gg-bac-draw"
          className="h-16 px-8 rounded-2xl text-2xl font-patient font-black text-white" style={{ background: "#2E8EC2" }}>
          <Shuffle className="w-7 h-7 mr-2" /> {cur ? "Nouvelle lettre" : "Tirer une lettre"}
        </Button>
      ) : <Done onEnd={onEnd} />}
    </div>
  );
}

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const norm = (w) => (w || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
const MAX_MISS = 7;

export function Pendu({ game, onEnd }) {
  const s = useStep(game.items);
  const [tried, setTried] = useState([]);
  const word = useMemo(() => norm(s.it?.word), [s.it]);
  const hint = useRef(false);
  const [showHint, setShowHint] = useState(false);
  if (s.end) return <Done onEnd={onEnd} />;
  const miss = tried.filter((l) => !word.includes(l)).length;
  const won = word.split("").every((l) => !/[A-Z]/.test(l) || tried.includes(l));
  const over = won || miss >= MAX_MISS || s.shown;
  const next = () => { setTried([]); setShowHint(false); hint.current = false; s.next(); };
  return (
    <div className="text-center" data-testid="gg-pendu">
      <Counter i={s.i} n={game.items.length} />
      <p className="text-6xl sm:text-7xl font-patient font-black tracking-[0.3em] text-gray-800 mb-4" data-testid="gg-word">
        {word.split("").map((l) => (!/[A-Z]/.test(l) || tried.includes(l) || over ? l : "_")).join("")}
      </p>
      <p className="text-2xl font-patient text-gray-500 mb-2" data-testid="gg-lives">
        Essais restants : {"●".repeat(Math.max(0, MAX_MISS - miss))}{"○".repeat(Math.min(MAX_MISS, miss))}
      </p>
      {showHint && <p className="text-2xl font-patient font-bold text-amber-700 mb-2">Indice : {s.it.hint}</p>}
      {won && <p className="text-4xl font-patient font-black text-emerald-700 my-4">Trouvé !</p>}
      {!won && over && <p className="text-3xl font-patient font-black text-amber-700 my-4" data-testid="gg-pendu-lost">Le mot était : {word}</p>}
      {!over && (
        <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto my-6">
          {ALPHA.map((l) => (
            <button key={l} disabled={tried.includes(l)} onClick={() => setTried((t) => [...t, l])} data-testid={`gg-letter-${l}`}
              className={`w-14 h-14 rounded-2xl text-2xl font-patient font-black border-4 ${tried.includes(l) ? (word.includes(l) ? "bg-emerald-100 border-emerald-300 text-emerald-700" : "bg-gray-100 border-gray-200 text-gray-300") : "bg-white border-gray-200"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {!over && !showHint && s.it.hint && <Button variant="outline" onClick={() => setShowHint(true)} className="h-14 px-6 rounded-2xl text-xl font-bold border-4" data-testid="gg-hint"><Lightbulb className="w-6 h-6 mr-2" /> Indice</Button>}
        {over ? <Next onClick={next} /> : <Reveal onClick={s.show} label="Donner le mot" />}
      </div>
    </div>
  );
}
